import { useEffect, useMemo, useState } from "react"

import { useDocumentContext } from "../../../../contexts/Document/DocumentContext"
import { useTranslation } from "react-i18next"

const SUPPRESS_DONE_MS = 800

const ContractLoader = ({
  setShowCards,
  loading: forceLoading = false,
  document: externalDoc,
}) => {
  const { t } = useTranslation()
  const ctx = useDocumentContext()
  const doc = externalDoc ?? ctx?.document ?? {}

  const step = doc?.step ?? null
  const status = doc?.status ?? "idle"
  const rawProgress = Number(doc?.progress)
  const safeProgress = Math.max(
    0,
    Math.min(100, Number.isFinite(rawProgress) ? rawProgress : 0)
  )

  const [suppressDone, setSuppressDone] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [lastDocumentId, setLastDocumentId] = useState(null)

  useEffect(() => {
    if (forceLoading) {
      setSuppressDone(true)
      const t = setTimeout(() => setSuppressDone(false), SUPPRESS_DONE_MS)
      return () => clearTimeout(t)
    }
  }, [forceLoading])

  // Track document ID changes to detect new documents
  useEffect(() => {
    if (doc?._id && doc._id !== lastDocumentId) {
      setLastDocumentId(doc._id)
      // When document ID changes, reset initialLoad to prevent showing old progress
      setInitialLoad(true)
    }
  }, [doc?._id, lastDocumentId])

  // Reset initial load flag when document changes
  useEffect(() => {
    if (doc && (status !== "idle" || step !== null)) {
      // Only reset initialLoad if the document is actually processing (not done)
      // and if it's not a very recent document (to avoid showing stale progress)
      const isRecentDocument =
        doc._createdAt && Date.now() - doc._createdAt.toDate().getTime() < 2000 // Less than 2 seconds old

      if (status !== "done" && !isRecentDocument) {
        setInitialLoad(false)
      }
    }
  }, [doc?._id, status, step, doc?._createdAt])

  // Reset initial load flag when forceLoading changes
  useEffect(() => {
    if (forceLoading) {
      setInitialLoad(true)
    }
  }, [forceLoading])

  const isUploadingPhase = useMemo(() => {
    if (forceLoading) return true
    if (status === "idle") return true
    if (status === "uploading" || step === "uploading" || step == null)
      return true
    return false
  }, [forceLoading, status, step])

  const isDoneStrict = status === "done" && safeProgress >= 100
  const isDone =
    isDoneStrict && !isUploadingPhase && !suppressDone && !initialLoad

  const getStepLabel = (step) => {
    const stepMap = {
      null: "loader.uploadingDocument",
      uploading: "loader.pleaseWait",
      downloading_file: "loader.downloadingFile",
      extracting_text: "loader.extractingText",
      detecting_language: "loader.detectingLanguage",
      chunking: "loader.chunking",
      analyzing: "loader.analyzing",
      merging: "loader.merging",
      saving_result: "loader.savingResult",
      done: "loader.done",
    }
    return stepMap[step] || "loader.pleaseWait"
  }

  const label = isDone ? t("loader.done") : t(getStepLabel(step))
  const barWidth = isDone ? 100 : Math.max(4, safeProgress)

  useEffect(() => {
    if (isDone) setShowCards?.(true)
  }, [isDone, setShowCards])

  return (
    <div className="flex flex-col items-center justify-center px-4 text-center gap-4 w-full">
      <style>{`
        @keyframes shimmer-slide-right {
   0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
        }

        .loader-track {
          background: rgba(0, 0, 0, 0.09);
        }

        .loader-bar {
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.15);
          position: relative;
          overflow: hidden;
        }

        .shimmerOverlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: 0.7;
          background: linear-gradient(
            270deg,
            rgba(255,255,255,0.00) 0%,
            rgba(255,255,255,0.10) 25%,
            rgba(255,255,255,0.65) 50%,
            rgba(255,255,255,0.10) 75%,
            rgba(255,255,255,0.00) 100%
          );
          background-size: 200% 100%;
          animation: shimmer-slide-right 1.6s linear infinite;
          will-change: background-position;
        }

        @media (prefers-reduced-motion: reduce) {
          .shimmerOverlay { animation: none; }
        }
      `}</style>

      <p className="text-lg text-gray-800 font-bold min-h-[1.5rem]">{label}</p>

      <div className="w-full h-4 loader-track rounded-full overflow-hidden shadow-inner">
        <div
          className="loader-bar h-full bg-dark-default transition-[width] duration-300 rounded-full"
          style={{ width: `${barWidth}%` }}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(isDone ? 100 : safeProgress)}
        >
          {!isDone && <div className="shimmerOverlay" aria-hidden="true" />}
        </div>
      </div>
    </div>
  )
}

export default ContractLoader
